import { doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../components/Firebase/firebase';
import { signOut } from 'firebase/auth';
import { auth } from '../components/Firebase/firebase';
import {
	getOrCreateDeviceId,
	getDeviceId,
	addSessionId,
	removeSessionId,
	hasSessionId,
	clearDeviceData
} from '../utils/deviceManager';

/**
 * Handles multi-device login logic
 * @param {Object} user - Firebase user object
 * @returns {Object} Result with success status and message
 */
export const handleMultiDeviceLogin = async (user) => {
	// [PARALLEL-WORLD] The parallel project has no Firestore — skip the multi-device/Firestore check,
	// treat the test user as an approved admin so login proceeds. (Original Firestore logic below is unreachable.)
	return {
		success: true,
		message: "Login successful",
		userData: { uid: user.uid, email: user.email, displayName: user.displayName || user.email || "Test User", status: "approved", role: "admin", allowedPages: [] },
		sessionId: "parallel",
	};
	try {
		const userDoc = await getDoc(doc(db, "users", user.uid));

		if (!userDoc.exists()) {
			throw new Error("User not found in Firestore.");
		}

		const userData = userDoc.data();

		// Check if user is approved
		if (userData.status !== "approved") {
			throw new Error("Your account is pending approval by an admin.");
		}

		const deviceId = getOrCreateDeviceId();
		const currentSessionIds = userData.sessionIds || [];

		// Check if this device is already registered
		if (currentSessionIds.includes(deviceId)) {
			// Device is already registered, allow login
			addSessionId(deviceId);
			return {
				success: true,
				message: "Login successful",
				userData,
				sessionId: deviceId
			};
		}

		// Check if user has reached the device limit (2 devices)
		if (currentSessionIds.length >= 2) {
			// Replace the oldest session (first in array) with the new device
			const updatedSessionIds = [deviceId, currentSessionIds[1]];

			await updateDoc(doc(db, "users", user.uid), {
				sessionIds: updatedSessionIds
			});

			addSessionId(deviceId);

			return {
				success: true,
				message: "Login successful (replaced existing session)",
				userData,
				sessionId: deviceId
			};
		}

		// Add new device to the session
		const updatedSessionIds = [...currentSessionIds, deviceId];

		await updateDoc(doc(db, "users", user.uid), {
			sessionIds: updatedSessionIds
		});

		addSessionId(deviceId);

		return {
			success: true,
			message: "Login successful",
			userData,
			sessionId: deviceId
		};

	} catch (error) {
		console.error("Multi-device login error:", error);
		throw error;
	}
};

/**
 * Handles multi-device logout logic
 * @param {string} userId - Firebase user ID
 * @returns {Promise<void>}
 */
export const handleMultiDeviceLogout = async (userId) => {
	try {
		const deviceId = getDeviceId();

		if (deviceId) {
			// Remove device from Firestore
			await updateDoc(doc(db, "users", userId), {
				sessionIds: arrayRemove(deviceId)
			});

			// Remove session from localStorage
			removeSessionId(deviceId);
		}

		// Clear all device data
		clearDeviceData();

		// Sign out from Firebase Auth
		await signOut(auth);

	} catch (error) {
		console.error("Multi-device logout error:", error);
		// Even if there's an error, clear local data and sign out
		clearDeviceData();
		await signOut(auth);
		throw error;
	}
};

/**
 * Validates if the current device session is still valid
 * @param {string} userId - Firebase user ID
 * @returns {Promise<boolean>} True if session is valid
 */
export const validateDeviceSession = async (userId) => {
	try {
		const deviceId = getDeviceId();

		if (!deviceId) {
			return false;
		}

		const userDoc = await getDoc(doc(db, "users", userId));

		if (!userDoc.exists()) {
			return false;
		}

		const userData = userDoc.data();
		const sessionIds = userData.sessionIds || [];

		return sessionIds.includes(deviceId);

	} catch (error) {
		console.error("Session validation error:", error);
		return false;
	}
};

/**
 * Migrates legacy sessionId to sessionIds array
 * @param {string} userId - Firebase user ID
 * @returns {Promise<void>}
 */
export const migrateLegacySession = async (userId) => {
	try {
		const userDoc = await getDoc(doc(db, "users", userId));

		if (!userDoc.exists()) {
			return;
		}

		const userData = userDoc.data();

		// If user has legacy sessionId but no sessionIds array
		if (userData.sessionId && !userData.sessionIds) {
			const deviceId = getOrCreateDeviceId();

			await updateDoc(doc(db, "users", userId), {
				sessionIds: [deviceId],
				sessionId: null // Remove legacy field
			});

			addSessionId(deviceId);
		}

	} catch (error) {
		console.error("Legacy session migration error:", error);
	}
}; 